import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { InsurancePolicyActionsCell } from 'src/components/insurance-policy/grid/InsurancePolicyActionsCell'
import { InsurancePolicyProductsCell } from 'src/components/insurance-policy/grid/InsurancePolicyProductsCell'
import type { InsurancePolicy } from 'src/interfaces/insurancePolicy'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import { insurancePolicyTableHeaderTitles } from './constants'
import { InsurancePoliciesTableFields } from './enums'

/**
 * Wiring only — date handling comes from utils/grid, actions from InsurancePolicyActionsCell.
 *
 * A factory rather than a module constant: the actions column closes over the page's edit and
 * delete handlers — and over nothing else. Both are stable `useCallback` references, so the
 * memoized result stays referentially stable for the life of the page. Callers must memoize.
 */
export const buildInsurancePolicyColumns = (
  onEditPolicy: (policy: InsurancePolicy) => void,
  onDeletePolicy: (policy: InsurancePolicy) => void,
  // `insurance_policies_manage` (ENG-1207 part 2). Rename and Delete both need it, so without it
  // the actions column is dropped; the row itself still opens the policy.
  canManage: boolean,
): Array<GridColDef<InsurancePolicy>> => {
  const columns: Array<GridColDef<InsurancePolicy>> = [
    {
      field: InsurancePoliciesTableFields.name,
      headerName: insurancePolicyTableHeaderTitles[InsurancePoliciesTableFields.name],
      flex: 2,
      minWidth: 200,
      sortable: true,
      // Name identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicy>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.name}
          </Typography>
        )
      },
    },
    {
      field: InsurancePoliciesTableFields.permanentId,
      headerName: insurancePolicyTableHeaderTitles[InsurancePoliciesTableFields.permanentId],
      width: 300,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicy>) => {
        // The PERMANENT id, not the version-specific `id`: it is what every route addresses and
        // what an admin pastes into a support ticket. A uuid is too long for the column, so it
        // truncates — `title` exposes the full value on hover for anyone who needs to copy it.
        return (
          <Typography variant="body2" css={tw`truncate`} title={params.row.permanentId}>
            {params.row.permanentId}
          </Typography>
        )
      },
    },
    {
      field: InsurancePoliciesTableFields.products,
      headerName: insurancePolicyTableHeaderTitles[InsurancePoliciesTableFields.products],
      flex: 3,
      minWidth: 220,
      // Chips over a nested array — nothing meaningful to sort or export by.
      sortable: false,
      disableExport: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicy>) => {
        return <InsurancePolicyProductsCell policyProducts={params.row.insurancePoliciesProducts} />
      },
    },
    {
      field: InsurancePoliciesTableFields.createdAt,
      headerName: insurancePolicyTableHeaderTitles[InsurancePoliciesTableFields.createdAt],
      width: 160,
      sortable: true,
      type: 'date',
      // Backend `createdAt` is unix SECONDS — parse first, then format the resulting Date.
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateOnly,
    },
    {
      field: InsurancePoliciesTableFields.actions,
      headerName: insurancePolicyTableHeaderTitles[InsurancePoliciesTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicy>) => {
        return (
          <InsurancePolicyActionsCell
            policy={params.row}
            onEdit={onEditPolicy}
            onDelete={onDeletePolicy}
          />
        )
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== InsurancePoliciesTableFields.actions
  })
}
